import type zhCN from './zh-CN'

/**
 * 香港繁體（zh-HK，2026-10-12）。類型聲明為 `typeof zhCN`，少鍵多鍵都是編譯錯誤。
 * 由 zh-CN.ts 經 OpenCC cn→hk 轉換、再按香港用語逐條自審（帳戶 / 登入 / 電郵 / 連結 / 短訊 / 伺服器 / 私隱 / 提款…，
 * 不用台灣的「軟體 / 網路 / 訊息」寫法）。改 zh-CN.ts 的文案時同步改這裏。
 * 頁腳等英文原文與 zh-CN 一樣保持英文；後端返回的展示字符串不翻譯、原樣渲染。
 * ⚠️ vue-i18n 消息裏 `@` 要寫成 `{'@'}`，`|` 不能出現（複數分隔符）。
 */
const messages: typeof zhCN = {
  app: {
    title: 'ZR Markets 用戶中心',
    brand: 'ZR Markets',
    portal: '投資者門戶',
  },

  common: {
    confirm: '確定',
    cancel: '取消',
    later: '稍後',
    close: '關閉',
    submit: '提交',
    save: '儲存',
    edit: '修改',
    back: '返回',
    next: '下一步',
    prev: '上一題',
    retry: '重試',
    refresh: '刷新',
    copy: '複製',
    copied: '已複製 ✓',
    copyFailed: '複製失敗，請手動複製',
    loading: '載入中…',
    today: '今天',
    clear: '清除',
    pickYearMonth: '選擇年份與月份',
    empty: '暫無資料',
    required: '必填',
    optional: '選填',
    all: '全部',
    status: '狀態',
    amount: '金額',
    currency: '幣種',
    account: '交易帳戶',
    manage: '管理',
    contactSupport: '聯絡客服',
    emailSent: '已發電郵',
    loadFailed: '載入失敗，請稍後重試',
    networkError: '網絡異常，請稍後重試',
    notAvailableYet: '該功能的後端介面尚未提供，暫不可用。',
  },

  nav: {
    companyInfo: '公司資訊',
    companyPeople: '人員認證與文件',
    // §4.1 選單命名以需求表為準，中英一一對應
    overview: '概覽',
    overviewBeforeOnboarding: '開戶',
    accounts: '帳戶管理',
    myAccount: '我的帳戶',
    profile: '個人資料',
    funding: '資金管理',
    deposit: '入金',
    withdraw: '出金',
    bankAccounts: '銀行帳號',
    reports: '報表',
    depositRecords: '存款記錄',
    withdrawalRecords: '提款記錄',
    platform: '平台下載',
    support: '幫助與支援',
    logout: '登出',
    menu: '選單',
    notReady: '介面未提供',
  },

  auth: {
    // 登入頁左半屏品牌區（與開戶站註冊頁同一套文案）
    brand: {
      title: '澳洲 ASIC 監管',
      licence: 'AFSL 562700',
      segregated: '客戶資金獨立存管。',
      f0: 'ASIC 監管 AFSL 562700',
      f1: 'MetaTrader 5 專業平台',
      f2: '註冊即開通模擬帳戶',
    },
    loginTitle: '登入用戶中心',
    email: '電郵地址',
    password: '登入密碼',
    remember: '在此裝置上保持登入',
    forgot: '忘記密碼？',
    login: '登入',
    noAccount: '還沒有帳戶？',
    openAccount: '立即開戶',
    // 不區分帳號不存在 / 密碼錯誤，防枚舉（§5.1）
    badCredentials: '電郵或密碼不正確',
    emailInvalid: '請輸入有效的電郵地址',
    passwordRequired: '請輸入登入密碼',
    loggingIn: '正在登入…',

    forgotTitle: '重置登入密碼',
    forgotDesc: '請輸入您的註冊電郵，我們會向該電郵發送重置連結。',
    sendLink: '發送重置連結',
    // 統一提示，不暴露電郵是否存在（§5.2）
    forgotSent: '若該電郵已註冊，重置連結已發送。',
    forgotSentHint: '重置連結 30 分鐘內有效。請檢查收件箱與垃圾電郵資料夾。',
    backToLogin: '返回登入',

    resetTitle: '設定新密碼',
    newPassword: '新密碼',
    confirmPassword: '確認新密碼',
    mismatch: '兩次輸入的密碼不一致',
    resetDone: '新密碼已生效',
    resetDoneDesc: '確認電郵已發送；其他已登入裝置已全部退出。',
    linkExpired: '該連結已失效',
    linkExpiredDesc: '重置連結僅 30 分鐘內有效，請重新申請一封。',
    requestAgain: '重新申請',

    changeTitle: '修改登入密碼',
    currentPassword: '目前密碼',
    changeDone: '密碼已更新，確認電郵已發送；其他已登入裝置已全部退出。',

    ruleTitle: '密碼需滿足：',
    ruleLength: '8–20 位',
    ruleUpper: '至少 1 個大寫字母',
    ruleLower: '至少 1 個小寫字母',
    ruleDigit: '至少 1 個數字',
    ruleNotSame: '不能與目前密碼相同',

    lockedTitle: '帳戶已臨時鎖定',

    // ---- 登入准入被拒（介面 0.10.0 / V1.4 §5.4）：三種拒絕三種去處 ----
    reviewPendingTitle: '您的開戶申請正在審核中',
    reviewPendingDesc: '我們已收到您的開戶申請，目前正在審核。審核期間暫時無法登入用戶中心——這不是您的操作有誤，也不需要重複嘗試。',
    reviewPendingNext: '審核完成後我們會透過電郵通知您，屆時即可正常登入。',
    reviewPendingContact: '如需了解進度，請聯絡客服：',
    denyApplicationDeclined: '很抱歉，您的開戶申請未能通過，暫無法登入用戶中心。如需了解詳情，請聯絡客服 {email}。',
    denyPortalDisabled: '您的帳戶暫不可用，請聯絡客服。',
    clientTypeMissing: '登入資訊不完整，請稍後重試；若反覆出現請聯絡客服。',

    // ---- 首登強制改密（V1.4 §5.2）：與「密碼已過期」同一個頁面，文案不同 ----
    firstLoginTitle: '首次登入，請設定您的登入密碼',
    firstLoginDesc: '您目前使用的是開通電郵裏的初始密碼。為了帳戶安全，請先設定一個只有您知道的新密碼。',
    // 首登頁沒有「目前密碼」欄，這一條本地無從判定，作為清單下方的一句說明呈現
    ruleNotSameInitial: '不能與初始密碼相同',
  },

  /** 公司相關人士認證清單（V1.4 §6A） */
  kycPeople: {
    /* 二期 P05：已通過的人有在途的重新認證（客服按要求更正了身份資料） */
    reverifyRequested: '待用新證件認證',
    /* 重新認證進度（2026-10-15，後端給）：已提交 / 已通過待生效 / 未通過可重拍 / 終拒 */
    reverifySubmitted: '新證件審核中',
    reverifyPassed: '新證件已通過 · 待生效',
    reverifyRetry: '新證件未通過 · 請重拍',
    reverifyRejected: '新證件未通過 · 請聯絡客服',
    titlePre: '公司相關人士身份認證',
    titleOpened: '人員認證與文件',
    passedOf: '已通過 {passed} / {total}',
    selfTag: '本人',
    noContact: '未登記聯絡方式',
    /* 按鈕文案按原型（V1.4 §6A 第 #5 條）：本人與其他人**同一句**「在此裝置認證」。
       同一列裏兩種叫法（「現在認證」/「在此認證」）看着像兩個不同功能 */
    verifyHere: '在此裝置認證',
    reverifyHere: '在此裝置重新認證',
    useMobile: '掃碼用手機認證',
    sendLink: '發送認證連結',
    resendLink: '重發認證連結',
    sendReverifyLink: '發送重新認證連結',
    inviteSent: '認證連結已發送至 {masked}，7 天內有效；{name} 完成後本頁狀態將自動更新。',
    inviteSelfHint: '這是您本人，請直接點「在此裝置認證」。',
    invitePassedHint: '該人員已完成認證，無需再發送。',
    inviteNoContact: '未登記聯絡方式，無法發送認證連結；請聯絡客服補充。',
    /* 需補件的兩層解釋（原型 §6A ④）：駁回原因由後端給，這兩句是固定的「不必從頭再來」 */
    resubmitSaved: '{name} 已提交的其他資料都已儲存，只需用上面任一方式重新完成未通過的那一步。',
    resubmitNotifiedBoth: '原因與重新提交指引已發送至您的電郵及該人電郵。',
    resubmitNotifiedRep: '原因與重新提交指引已發送至您的電郵。',
    reviewNote: '資料已提交，我們正在人工核對，無需重新提交；有結果會以電郵通知您。',
    /* 未通過是終態：三條自助入口都已關閉，只能由我們重新開放。不披露原因（與統一婉拒同口徑） */
    failedNote: '該人員本次認證未能通過，需由我們協助處理後才能再次認證。請',
    /* 只陳述起點，不說「已提交/審核中」：有起點沒結論有三種可能，我們分不清是哪一種 */
    startedAt: '已於 {time} (UTC) 發起認證',
    /* 返回清單後仍在等結論時的行內提示：只說我們在做的事，不替客戶斷言他提交沒提交 */
    syncing: '正在檢查最新狀態…',
    /* 遷移遺留：這一檔是我們缺欄位，不是客戶要辦的事——說清楚並給客服入口，別讓他去重認 */
    notCollectedNote: '該人員的證件有效期我們尚未登記，認證結果不受影響。如需補錄，請',
    docsToggle: '認證與文件',
    docId: '身份證明',
    docPoa: '地址證明',
    docAml: 'AML 篩查',
    validUntil: '有效期至 {date}',
    nextUpdate: '下次更新 {date}',
    /* 二期 §14.7：地址證明是 Sumsub 獨立的第三步 */
    poaVerified: '已驗證',
    amlPassed: '已通過',
    daysLeft: '還剩 {n} 天，請在到期前重新認證',
    expiredNow: '請立即重新認證',
    docsNote:
      '根據我們的帳戶政策，身份證件須在到期前重新認證，地址證明按週期更新；到期前我們會提前提醒。身份證明與地址證明在同一次認證中採集；AML 篩查由系統自動完成，無需另行提交文件。',
    allPassed: '全部人員已通過身份認證。我們正在為公司開立 MT5 交易帳戶，完成後會以電郵通知您。',
    someFailed: '有人員的身份認證未通過，帳戶暫無法開立。如需了解詳情，',
    empty: '暫無相關人士記錄。如有疑問請聯絡客服。',
    /* 原型頁腳的兩句口徑缺一不可：只有授權代表有登入帳號，其餘人靠這三條路（V1.4 §6A） */
    footPre:
      '用戶中心僅授權代表可登入，公司相關人士不開設登入帳號。每位人員須由本人完成證件拍攝與活體檢測：可在此裝置直接認證、用本人手機掃碼認證，或透過發送到其電郵的認證連結自行完成（7 天內有效，過期後可再次發送）。每位人員只需完成一次。',
    footOpened: '證件到期前 60 天我們會以電郵提醒；證件即將到期或已過期時，這裏會出現重新認證入口。',
  },

  /** 認證前確認卡（V1.4 §6A） */
  kycConfirm: {
    title: '開始前，請核對以下資料',
    desc: '這些是我們為您登記的資料。請確認與您即將上傳的證件一致。',
    reverifyDescPerson: '我們正按貴公司的要求更新 {name}（{role}）的身份資料。請使用新的證件完成這次認證（不要用舊證件），通過後經審核即更新。',
    descPerson: '這些是我們為 {name}（{role}）登記的資料。請確認與即將上傳的證件一致。',
    name: '姓名',
    dob: '出生日期',
    address: '居住地址',
    idType: '證件類型',
    idNumber: '證件號碼',
    idExpiry: '證件有效期至',
    idCountry: '證件簽發國',
    warn: '如與證件不一致，認證會轉為人工審核並延長處理時間。資料有誤請先聯絡客服更正，再開始認證。',
    proceed: '資料無誤，繼續',
  },

  /** 認證前預檢頁（零售，V1.4 §6A） */
  kycStart: {
    title: '開始身份認證',
    titleReverify: '重新認證',
    stepN: '第 {n} 步',
    // 原型 V1.4.1「認證內容」三步：證件拍攝 → 活體檢測 → 地址證明
    step1: '證件拍攝：護照或駕駛執照，按提示拍攝正反面',
    step2: '活體檢測：短視像自拍，須由您本人完成',
    step3: '地址證明：3 個月內的銀行結單或水電帳單',
    recordTitle: '我們登記的資料',
    name: '姓名',
    dob: '出生日期',
    address: '居住地址',
    country: '居住國家 / 地區',
    warn: '請確認以上資料與您即將上傳的證件一致。不一致會轉為人工審核並延長處理時間。',
    proceed: '資料無誤，開始認證',
    mismatchHint: '資料有誤？請先聯絡客服更正後再開始，認證過程中無法修改。',
    /* 二期 P05：敏感資訊更正發起的重新認證——登記資料是舊值，不展示 */
    useNewId: '請使用新證件',
    proceedNewId: '用新證件開始認證',
    useNewIdDesc: '我們正按您的要求更新身份資料。請使用新的證件完成這次認證（不要用舊證件），通過後經審核即更新到您的帳戶。',
    // 恢復登入時客服要求重新提交（決策 029，D-98）：登記資料沒在改，照常展示；不說「新證件」。文案待產品確認
    rekycTitle: '請按客服要求重新提交認證資料',
    rekycDesc: '客服已要求您重新完成身份認證（如身份證明、地址證明）。請先核對下方登記資料，再按指引拍攝上傳，提交後我們會盡快審核。',
    /* 已提交待人工複核：這一檔不給「開始認證」，後端此時也拒籤 SDK token */
    underReview: '您的資料已提交，我們正在人工核對（通常 1 個工作天內），暫時不需要重新提交。結果會以電郵通知您。',
  },

  /** SDK 承載面板 */
  kycSdk: {
    verifying: '正在認證：{name}',
    selfTag: '本人',
    backToList: '返回清單',
    /* 合規提示：代表可以替他拉起 SDK，但拍攝與活體必須本人做（原型藍條同款） */
    byPersonHint: '請確保由本人完成證件拍攝與活體檢測。',
    byYouHint: '請由您本人完成證件拍攝與活體檢測。',
    /*
     * 這裏原來還有 stepLine / step1 / step2 / countryHint 四條，已刪：
     * 步驟由 Sumsub 自己的介面顯示（原型 P8 就是它的介面還原，步驟片在它的頭部裏），
     * 我們外面再寫一個不但重複，而且那個 n 取自「SDK 起來沒起來」，一進去就顯示 2/2；
     * 「簽發國預設為 X」更是我們沒做的事（那個值從沒傳給 Sumsub）。
     */
    /**
     * 認證元件（第三方 SDK 腳本）沒下載下來。與「認證通道準備中」不是一回事：
     * 那個是我們籤不出 token，這個是瀏覽器取不到 SDK 腳本——該做的事不同，
     * 混成同一句「載入失敗」就分不出來了。實測這條 CDN 在受限網絡下會間歇性超時。
     */
    sdkLoadFailed: '認證元件載入失敗。請檢查網絡後重試；如反覆失敗，可能是網絡限制了外部認證服務，請聯絡客服。',
    /** 腳本下來了但起不來：不是客戶能自助解決的，直接引導找客服 */
    sdkBroken: '認證元件無法啟動，請聯絡客服協助處理。',
  },

  /** 「用手機繼續」二維碼 */
  kycQr: {
    title: '掃碼用手機認證',
    desc: '請 {name} 用手機掃描下方二維碼，按提示完成認證。',
    scanHint: '用手機相機掃描即可打開，無需註冊帳號。',
    /* 別寫「舊連結隨即失效」：後端 issue() 不作廢舊 token，剛發出去的那封電郵裏的連結照樣能用 */
    expire: '本二維碼為該人員專屬，可重複掃描，7 天內有效。',
    linkLabel: '或複製連結發送給本人',
    refresh: '重新生成',
    review: '已提交，正在審核中。審核完成後本頁會自動更新。',
    resubmit: '需要重新拍攝證件。請按手機上的提示重新提交。',
    passed: '認證已通過',
    passedHint: '{name} 的身份認證已完成。',
    reverifyPassed: '新證件已通過認證',
    reverifyPassedHint: '{name} 的新證件已通過，資料更正經審核後生效。',
    reverifyRejected: '{name} 的新證件未能通過認證，請聯絡客服 {email}。',
    expired: '這條連結已失效，請點「重新生成」獲取新的二維碼。',
  },

  /** 認證連結落地頁（免登入） */
  kycInvite: {
    /* 二期 P05：已通過的人被要求用新證件重新認證 */
    reverifyDesc: '{company} 正在更新您的身份資料，需要您用新證件完成身份認證。整個過程約需 3 分鐘，無需註冊帳號。',
    reverifyWarn: '請準備好新的身份證件（不要用舊證件）並確保光線充足。',
    title: '完成身份認證',
    desc: '{company} 正在開立交易帳戶，需要您完成一次身份認證。整個過程約需 3 分鐘，無需註冊帳號。',
    role: '角色',
    company: '所屬公司',
    warn: '請準備好您的身份證件並確保光線充足。資料與證件不一致會轉為人工審核並延長處理時間。',
    review: '已提交，正在審核中。您可以關閉此頁面，結果會通知到公司的授權代表。',
    resubmit: '需要重新拍攝證件，請按頁面提示重新提交。',
    /* 重新打開連結時的「審核中」整卡：與上面那句 review 不是一件事——那句是剛拍完停在本頁時的提示 */
    reviewTitle: '資料審核中',
    reviewDesc: '您已經提交過一次認證，我們正在人工核對，暫時不需要重複提交。結果會通知到 {company} 的授權代表。',
    doneTitle: '認證已完成',
    doneDesc: '感謝您的配合。結果會通知到 {company} 的授權代表，您無需再做任何操作。',
    /* 終態（未通過 / 已移除）：不披露原因——這一頁憑一條 token 就能打開 */
    endedTitle: '本次認證已結束',
    endedDesc: '這條連結對應的認證已經結束，暫時無法繼續。請聯絡 {company} 的授權代表了解後續安排。',
    expiredTitle: '連結無效或已過期',
    expiredDesc: '認證連結的有效期是 7 天。請聯絡向您發送連結的公司授權代表重新發送一份。',
    contact: '如有疑問，也可以聯絡我們的客服：',
  },

  /** MT5 開通等待（V1.4 §5.5） */
  mt5Wait: {
    openingTitle: '交易帳戶開通中',
    openingDesc: '您的資料已全部通過，我們正在為您開立 MT5 交易帳戶。完成後會立即以電郵通知您。',
    openingBanner: '交易帳戶正在開通中，完成後我們會立即電郵通知您。',
    // failedTitle / failedDesc / lockedNote / refresh 隨 Mt5WaitView 一起刪：說明頁已無入口，彈窗版文案在下面
    // 首頁橫幅 + 說明彈窗標題（用戶 09-20 口徑）
    banner: '您的 MT5 交易帳戶暫未能開通',
    failedDialogBody: '您的開戶步驟已全部完成，但交易帳戶未能自動開立。我們已收到該問題，請聯絡客服協助處理，我們會為您完成開通。',
    // 開通中：側欄 / 能力不足的點擊彈這個（用戶 09-20 口徑）
    openingDialogTitle: '您的交易帳戶正在開通中',
    openingDialogBody: '您的開戶步驟已全部完成。開通通常幾分鐘內完成，帳戶就緒後我們會將 MT5 登入資訊發送至您的電郵；如 1 個工作天內仍未收到，請聯絡客服。',
  },

  /** 公司資訊（V1.4 §6B） */
  company: {
    peopleHint: '以下每位人員均須各自完成 Sumsub 身份認證（證件 + 活體檢測 + 地址證明）；標籤為該人的身份認證結果。',
    title: '公司資訊',
    entityTitle: '公司主體',
    legalName: '公司法定名稱',
    acn: 'ACN',
    incorporation: '成立日期',
    address: '註冊辦公地址',
    classification: '客戶分類',
    peopleTitle: '授權代表與公司相關人士',
    kycState: '身份認證：{state}',
    gotoPeople: '前往身份認證清單 ›',
    noPeople: '暫無相關人士記錄。',
    // 原型 V1.4.1 說明條口徑
    // 電郵走 {email} 佔位（useSupport().email 唯一來源），語言包不再寫死地址
    readOnlyNote: '法律實體名稱、ACN、董事與實益擁有人、授權代表等關鍵資訊如需變更，請聯絡客服 {email} 並提供相應的 ASIC 變更文件。開戶時提交的申請文件由我方按記錄保留政策歸檔，如需副本請聯絡客服。',
  },

  pwd: {
    // §5.3 登入密碼有效期與到期提醒。一律表述為「我們的帳戶政策」
    rowLabel: '登入密碼',
    lastChanged: '上次修改 {date}',
    daysLeft: '剩餘 {n} 天到期',
    expired: '已過期，需立即修改',
    bannerText: '根據我們的帳戶政策，用戶中心登入密碼每 90 天需更新一次。您的密碼將在 {n} 天後到期，請及時修改。',
    bannerAction: '修改密碼',
    forceTitle: '請更新登入密碼後繼續',
    forceDesc: '根據我們的帳戶政策，用戶中心登入密碼每 90 天需更新一次。您的密碼已到期，請設定新密碼後繼續使用。（此密碼與 MT5 交易密碼不同。）',
    modalTitle: '登入密碼即將到期',
  },

  overview: {
    accountIdOpening: '—（開通中，稍後分配）',
    tagOpening: '開通中',
    tagMt5Failed: '暫未能開通',
    bannerOnboardCorp: '還需 {n} 人完成身份認證，全部通過後我們會自動為公司開立交易帳戶。',
    corpTask: '完成身份認證',
    corpTaskDesc: '公司的授權代表、董事與最終實益擁有人都要各自完成一次身份認證。',
    corpTaskLink: '查看認證清單 ›',
    corpTaskFoot: '全部人員通過認證並開立 MT5 帳戶後，即可使用資金管理、報表與平台下載。',
    corpTaskCta: '查看認證清單',
    greeting: '你好，{name}',
    emailLine: '電郵：{email}',

    // §6.1.2 待開戶任務彈窗
    tasksTitle: '完成開戶的最後步驟',
    task1: 'CFD 知識測試',
    task1Desc: '10 道題，答對 8 道及以上即通過',
    task2: '身份認證',
    task2Desc: '上傳證件並完成人臉識別，系統自動審核',
    // 審核中（自動審核 / 轉人工）替換掉上面那句「上傳證件…」——資料已經交了，再讓他看怎麼交是誤導（用戶 09-20）
    task2Reviewing: '已提交，我們正在人工核對（通常 1 個工作天內），您無需重新提交。',
    // 需補件（最新 KYC 記錄 RED、進度仍待開戶）：標籤 / 說明 / 按鈕 / 首頁橫幅（用戶 09-20）
    taskResubmit: '需補件',
    task2Resubmit: '證件未能通過審核，請重新拍攝並提交；其餘資料已儲存。',
    startKycResubmit: '重新拍攝證件',
    bannerResubmit: '您的身份證件未能通過審核，請重新拍攝並提交',
    task2Locked: '請先通過知識測試',
    // 公司戶免測（介面 0.8.0 exempt）
    taskExempt: '公司戶免測',
    task1Exempt: '公司帳戶無需知識測試，由授權代表直接完成身份認證',
    /*
     * 別寫成「只需授權代表完成身份認證」：MT5 的門檻是**全員通過**（現任授權代表 + 全部董事 / UBO），
     * 而且這句話就擺在下面那張寫着「已通過 1 / 3」的任務卡上方，自己打自己。
     * 公司戶與零售的區別是免知識測試與免適當性問卷，不是「只認代表一個人」。
     */
    tasksDescCorporate: '公司帳戶無需知識測試，只要全部公司相關人士完成身份認證，我們就會自動為您開立 MT5 真實交易帳戶。',
    taskTodo: '待完成',
    taskDoing: '進行中',
    taskReviewing: '審核中',
    taskDone: '已完成',
    startQuiz: '開始知識測試',
    startKyc: '繼續身份驗證',
    ctaFinish: '完成最後步驟以開通帳戶',
    // 帳戶設定中（介面 0.8.4 accountProvisioning）
    tagProvisioning: '帳戶設定中',
    tasksDescProvisioning: '兩項任務均已完成，我們正在為您開立 MT5 真實交易帳戶，完成後會立即以電郵通知您。',
    tasksDescOpenFailed: '很抱歉，您的交易帳戶未能開立。如需了解詳情，請聯絡客服 {email}。',
    bannerOpenFailed: '很抱歉，您的交易帳戶未能開立，請聯絡客服了解詳情',

    // §6.1.1 待提交引導彈窗
    applyTitle: '請先完成開戶申請',
    applyDesc: '該功能需在開戶完成後使用。您的開戶申請尚未提交，請從中斷處繼續填寫並提交。',
    applyProgress: '開戶申請進度：已完成 {done}/5 步 · 目前進行到 第 {step} 步 · {name}',
    applyStep1: '提交開戶申請',
    applyStep2: '完成 CFD 知識測試與身份認證',
    applyStep3: '系統自動為您開立 MT5 真實交易帳戶',
    continueApply: '繼續開戶',
    bannerDraft: '您有一份未完成的開戶申請',
    // 粗標題後的進度句（原型橫幅口徑）；步名 {name} 是後端渲染的展示字元串，不翻譯
    bannerDraftDesc: '已完成 {done}/5 步，目前進行到「{step}. {name}」。此前填寫的資訊已儲存，可直接繼續。',
    tagDrafting: '申請填寫中',

    // 帳戶卡
    cfdAccount: 'CFD 帳戶',
    accountIdPending: '—（成功開戶後分配）',
    fundsLabel: '資金',
    marginLabel: '保證金',
    availableLabel: '可用',
    plLabel: '盈虧',
    balance: '餘額',
    equity: '淨值',
    floatingPl: '浮動盈虧',
    freeMargin: '可用保證金',
    marginLevel: '保證金水平',
    accountNote: '以上數據為所選交易帳戶 {login}（{type}）。',
    leverageNote: '負餘額保護已啟用',
    // 概覽對齊原型 v-dash
    balanceOf: '餘額（{cur}）',
    tileDepositDesc: '銀行轉帳（AUD / USD）',
    tileWithdrawDesc: '轉帳至您本人已審核的銀行帳戶',
    acctMeta: '帳戶 ID：{login} · 幣種 {currency}',
    accountDetails: '帳戶詳情',
    colTime: '時間',
    colType: '類型',
    colMethod: '方式',
    colCurrency: '幣種',
    colAmount: '金額',
    colStatus: '狀態',

    bannerOnboard: '完成您的開戶',
    // §7 受限模式首頁橫幅（按模式分文案；按鈕彈對應說明彈窗）
    bannerSuit1: '暫未通過適應性評估。您可於 {date} 後重新申請，到期我們會電郵提醒您。',
    bannerKtCooling: '知識測試未通過，可於 {date} 後重考（屆時電郵提醒您）。',
    bannerKtReady: '知識測試的等待期已結束，現在可以重新參加測試。',
    bannerDup: '您的開戶申請正在人工審核中，暫時無法繼續開戶流程。',
    bannerTerminal: '很抱歉，您暫不符合我們的開戶條件。如有疑問請聯絡客服。',
    // SUIT2 專句（產品 09-20）
    bannerTerminalSuit2: '根據目標市場確定（TMD）評估，我們無法為您開立帳戶。如有疑問請聯絡客服。',
    // AGE 專句（產品 09-20）
    bannerTerminalAge: '很抱歉，您的申請未能通過。',
    viewDetails: '查看說明',
    // §9 最近記錄（最近出入金摘要；交易記錄待二期報表介面）
    recentTitle: '最近記錄',
    recentEmpty: '暫無出入金記錄。',
    recentViewAll: '查看全部報表 ›',
    typeDeposit: '入金',
    typeWithdraw: '出金',
    // §6.4 開通失敗兜底：開戶已完成但帳戶尚未出現
    provisioningBanner: '您的資料已全部通過，交易帳戶正在設定中，完成後我們會立即電郵通知您。',
    bannerMore: '＋ 還有 {n} 項待處理',
    bannerCollapse: '收起',

    quickDeposit: '入金',
    quickWithdraw: '出金',

    staleData: '數據更新於 {time}',
    // 批量取數時單個帳戶失敗：必須說'取不到'，不能顯示成 0（0 會被當成真的沒錢）
    fundsUnavailable: '該帳戶的資金數據暫時無法獲取，請稍後刷新。',
    noAccountYet: '暫無帳戶',
  },

  restricted: {
    // §7 受限模式：所有拒絕 / 審核彈窗僅含「聯絡客服 + 確定」
    // 原型 V1.4.1 彈視窗徑（用戶 09-20）；日期取介面 suitabilityCooldownUntil
    suit1Title: '暫未通過適應性評估',
    suit1Body: '根據您的作答情況，您暫不符合我們 CFD 產品的目標市場確定（TMD）要求。您的申請已暫停，可於 {date} 後重新申請；到期我們會透過電郵提醒您。',
    reapply: '重新申請',
    reapplied: '已為您重新開啟申請',
    // 非終態的兜底：門禁漏判 / 狀態不同步時，絕不能對正常客戶說「不符合開戶條件」
    unavailableTitle: '該功能目前不可用',
    unavailableBody: '請稍後重試。如有疑問，請聯絡客服 {email}。',
    terminalTitle: '很抱歉，您暫不符合我們的開戶條件',
    terminalBody: '如需了解更多或辦理帳戶關閉，請聯絡客服 {email}。',
    // SUIT2 專句（產品 09-20）
    suit2Title: '我們無法為您開立帳戶',
    suit2Body: '很抱歉，根據目標市場確定（TMD）評估，我們無法為您開立帳戶。該結果為最終結論，不再提供重新申請。如有疑問請聯絡客服；如需關閉帳戶，請聯絡客服辦理。',
    // AGE 專句（產品 09-20）
    ageTitle: '很抱歉，您的申請未能通過',
    ageBody: '很抱歉，您的申請暫未符合我們的開戶要求。如您認為這是誤判或需要了解更多資訊，請聯絡我們的客服團隊。',
    // 查重核實中（DUP，0.13.5 起可登入；D-96）：中性「審核中」，不是終態
    dupTitle: '您的開戶申請正在審核中',
    dupBody: '我們已收到您的開戶申請，目前正在人工審核。審核期間暫時無法繼續開戶流程，帳戶資料可正常查看。審核完成後我們會透過電郵通知您；如需了解進度，請聯絡客服 {email}。',
    kt1Title: '知識測試冷卻中',
    // 產品 09-20 給的口徑；等待時長按次數分兩句（第 1 次 48 小時 / 第 2 次 6 個月），具體日期取介面 nextAvailableAt
    kt1Body: '您的知識測試未達到通過線（80%）。為了讓您有時間複習相關文件，48 小時後方可重考（{date} 之後）；冷卻結束我們會透過電郵提醒您，屆時從「完成開戶的最後步驟」即可再次進入測試。',
    kt2Body: '您的知識測試未達到通過線（80%）。為了讓您有時間複習相關文件，6 個月後方可重考（{date} 之後）；冷卻結束我們會透過電郵提醒您，屆時從「完成開戶的最後步驟」即可再次進入測試。',
    ktBlockedTitle: '知識測試次數已用完',
    readOnlyNote: '目前帳戶為唯讀狀態，資料修改請聯絡客服。',
  },

  quiz: {
    title: 'CFD 知識測試',
    // 公司戶免測進場頁（介面 0.8.0 exempt）
    exemptTitle: '公司帳戶無需進行知識測試',
    exemptBody: '知識測試僅面向個人客戶。請授權代表直接完成身份認證，通過後我們會自動開立 MT5 真實交易帳戶。',
    // 進場頁僅展示兩條規則（§6.2）
    ruleQuestions: '共 10 道題，答對 8 道及以上（80%）即通過',
    ruleCooldown: '共有 3 次機會：第 1 次未通過需等待 48 小時，第 2 次未通過需等待 6 個月，第 3 次未通過將無法繼續開戶。',
    attemptNotice: '這是您的第 {n} 次測試機會（共 {max} 次）。',
    start: '開始測試',
    resume: '繼續上次的測試',
    progress: '第 {cur} / {total} 題',
    submit: '提交答卷',
    submitConfirmTitle: '提交答卷',
    submitConfirm: '提交後不可更改，確定提交嗎？',
    unanswered: '還有 {n} 道題未作答',
    passedScore: '通過（{score}/10）',
    passedNext: '接下來完成身份認證，即可開立交易帳戶。',
    continueKyc: '繼續身份驗證',
    // 失敗不顯示錯題與正確答案（§6.2）
    failedTitle: '未達到通過線',
    failedRetryAt: '您可於 {date} 之後重新測試。',
    failedFinalTitle: '很抱歉，您暫不符合我們的開戶條件',
    failedFinalBody: '本次為第 3 次未通過，我們無法繼續為您辦理開戶。',
    mailSent: '結果與說明已發送至您的電郵。',
    coolingUntil: '可於 {date} 後重新測試',
    noAttemptsLeft: '次數已用完',
    alreadyPassed: '您已通過知識測試',
    resumeHint: '已為您恢復上次的答題進度。',
    answerFailed: '答案未能儲存，請重新選擇。',
  },

  /** 敏感資訊更正的重新認證進度卡（預檢頁 / 認證頁 / 免登入頁共用，2026-10-15；進度由後端給，不向客戶透露識別值不一致） */
  reverifyCard: {
    submittedTitle: '新證件已提交，正在審核',
    submittedDesc: '我們正在審核您的新證件。資料更正處理完成後會以電郵通知您，您可以先離開本頁。',
    submittedDescInvite: '感謝您用新證件完成認證，我們正在審核；{company} 的授權代表會收到結果。您可以關閉本頁。',
    passedTitle: '新證件已通過認證',
    passedDesc: '資料更正經審核後生效，屆時會以電郵通知您。',
    passedDescInvite: '資料更正經審核後生效，{company} 的授權代表會收到通知。您可以關閉本頁。',
    retryTitle: '新證件未能通過認證',
    retryDesc: '請檢查證件後重新拍攝上傳；如仍無法通過，請聯絡客服 {email}。',
    rejectedTitle: '新證件未能通過認證',
    rejectedDesc: '請聯絡客服 {email}，我們會協助您處理。',
    submittedNow: '新證件已提交，我們正在審核。您可以關閉本頁。',
    // 恢復登入時客服要求重新提交（決策 029，D-98）：交完、等結論。文案待產品確認
    rekycSubmittedTitle: '資料已提交，正在審核',
    rekycSubmittedDesc: '我們正在審核您按客服要求重新提交的資料，您可以先離開本頁。',
  },

  kyc: {
    /* 二期 P05：敏感資訊更正發起的重新認證——進度由後端給（reverifyCard），這裏只留「待認證」那一檔的說明 */
    reverifyIntro: '我們正按您的要求更新身份資料，請用新的證件完成身份認證。',
    /* 證件臨期 / 已過期的重新認證（認證頁 ?renew=1）：不出開戶完成頁 */
    renewIntro: '您的證件即將到期或已過期，請用有效證件重新完成身份認證。',
    renewPassedTitle: '證件已更新',
    renewPassedDesc: '您已用有效證件完成認證，證件有效期會隨之更新。',
    // 恢復登入時客服要求重新提交（決策 029，D-98）：認證頁待認證時的說明、出了結論（要求已清掉）後的結果頁。文案待產品確認
    rekycIntro: '客服已要求您重新提交認證資料，請按指引完成身份認證。',
    rekycPassedTitle: '認證已完成',
    rekycPassedDesc: '您按客服要求重新提交的資料已通過認證。',
    resubmitTitle: '請重新拍攝證件',
    resubmitBody: '我們收到了您的資料，但需要您重新提交一次。請按下方提示重新拍攝證件，注意光線充足、四角完整、避免反光。',
    title: '身份認證',
    intro: '請準備好您的身份證件。整個過程約 3 分鐘，由我們的認證服務商完成採集與自動審核。',
    startBtn: '開始認證',
    mobileTitle: '用手機繼續',
    mobileHint: '在手機上打開本頁即可用手機相機完成拍攝，網頁版會即時同步進度。',
    // §6.3 各狀態頁面表現
    reviewing: '資料已提交，正在自動審核（通常幾分鐘）',
    reviewingHint: '您可以停留等待，也可以先離開。結果會以電郵通知您。',
    manual: '正在人工審核',
    manualHint: '我們正在進一步核實您的資料，結果將以電郵通知。',
    passed: '身份認證已通過',
    passedNext: '我們正在為您開立 MT5 真實交易帳戶。',
    rejected: '很抱歉，本次認證未能通過',
    // 硬拒絕統一婉拒，不披露原因（§6.3）
    rejectedBody: '如需了解更多，請聯絡客服 {email}。',
    resubmit: '重新拍攝',
    // kyc/token 恆返回 50311 的降級態
    channelDownTitle: '認證通道準備中',
    channelDownBody: '身份認證服務正在設定中，暫時無法開始。我們會在開通後以電郵通知您，您也可以稍後回來重試。',
    quizFirst: '請先通過 CFD 知識測試',
    docExpiry: '證件有效期',
  },

  onboardDone: {
    currencyNote: '（按您申請時的選擇）',
    nbpValue: '已啟用 — 您的虧損不會超過帳戶內資金',
    riskWarn: 'CFD 為複雜的高風險槓桿產品，您可能迅速產生虧損；請僅在充分理解相關風險後進行交易。',
    title: '開戶完成',
    desc: '您的 MT5 真實交易帳戶已開立，可以開始入金交易。',
    login: 'MT5 帳號',
    server: '伺服器',
    nbp: '負餘額保護已啟用',
    mailSent: '開戶完成電郵已發送，內含 MT5 登入資訊。',
    firstDeposit: '立即入金',
    // MT5 介面異常時的兜底（§6.4）
    provisioning: '帳戶設定中，完成後電郵通知',
    provisioningHint: '您的資料已全部通過，我們正在開立交易帳戶。完成後會立即以電郵通知您。',
    // KYC 通過但帳戶還沒開出來：不能說「開戶完成」
    kycPassedTitle: '身份認證已通過',
    kycPassedDesc: '您的資料已全部通過審核，交易帳戶開立中。',
    // CRM 人工確認不開（accountOpening=FAILED）：中性說明，不披露原因
    openFailedTitle: '帳戶未能開立',
    openFailedDesc: '很抱歉，您的交易帳戶未能開立。如需了解詳情，請聯絡客服 {email}。',
  },

  // 介面 7.0：MT5 交易密碼重置（電郵連結落地頁 /mt5-password/reset）
  mt5pwd: {
    resetTitle: '設定新的 MT5 交易密碼',
    resetDesc: '此密碼用於登入 MT5 交易終端，與用戶中心登入密碼無關；設定後不會退出用戶中心。',
    newPassword: '新交易密碼',
    ruleLength: '8–15 位',
    // @ 必須寫成 {'@'} 字面量：vue-i18n 把裸 @ 當連結消息（@:key）的起頭，整條消息編譯失敗，
    // 拋在 PasswordRuleHint 的渲染裏、把整塊規則說明弄沒了（2026-09-16 上過一次 dev）。同 readOnlyNote 的電郵寫法
    ruleSymbol: '至少 1 個符號（如 # {\x27@\x27} !）',
    // MT5 只收 ASCII 可列印字元（後端 Mt5PasswordRules.isAsciiPrintable）：中文、全角符號、空格都不行
    ruleAscii: '僅限英文字母、數字與鍵盤符號，不含空格',
    done: '新交易密碼已生效',
    doneDesc: '確認電郵已發送。請使用新密碼登入 MT5 交易終端。',
    backToPortal: '返回用戶中心',
    linkExpiredDesc: '重置連結僅 30 分鐘內有效且只能使用一次。請登入用戶中心，在「我的帳戶」重新發起。',
    // 50311：結果未確認、連結不放回——不給重試按鈕
    unknownTitle: '重置結果暫未確認',
    unknownDesc: '您的交易密碼可能已經更新。請先用新密碼嘗試登入 MT5 交易終端；若無法登入，請在「我的帳戶」重新發起一次重置。為避免混淆，本連結不再可用。',
    goMyAccount: '前往我的帳戶',
  },

  account: {
    accountName: '帳戶名稱',
    corpTypeSuffix: '（公司客戶 Wholesale）',
    sophCorpTypeSuffix: '（公司客戶 Sophisticated）',
    // 槓桿按品種類別不同（業務 CP-002）：不再顯示帳戶級 1:30，給官網槓桿表外鏈
    leverageByProduct: '按品種類別不同',
    leverageTable: '查看槓桿表',
    // 形態按原型 V1.4.1 #v-myaccount：麪包屑 / 副標題 / 查看帳戶下拉 / 四張統計卡 / 帳戶詳情表
    pickAccount: '查看交易帳戶',
    detailsTitle: '帳戶詳情',
    mt5Login: 'MT5 帳號',
    retailTypeSuffix: '（零售 Retail）',
    sophIndTypeSuffix: '（個人 Sophisticated）',
    baseCurrency: '基礎貨幣',
    currency: '貨幣',
    nbp: '負餘額保護',
    nbpOn: '已啟用',
    nbpNotApplicable: '不適用',
    nbpNote: '負餘額保護：您的帳戶虧損不會超過帳戶餘額。',  // 開戶完成頁 §6.4 仍在用
    title: '我的帳戶',
    server: '伺服器',
    type: '帳戶類型',
    leverage: '槓桿',
    marginWarn: '{type} · {login}　保證金水平 {level}，已觸及 {line} 追加保證金水平（PDS）',
    marginDanger: '{type} · {login}　保證金水平 {level}，已觸及 {line} 強制平倉水平，持倉可能被自動平倉',
    depositNow: '立即入金',
    frozenNote: '該帳戶目前已凍結，臨時禁止交易：不能開倉也不能平倉、暫不能入金，仍可出金與查看報表。',
    disabledNote: '該帳戶已停用，如需恢復請聯絡客服。',
    closedNote: '該帳戶已關閉。',
    changingNote: '帳戶變更處理中，請稍後刷新查看。',
    pendingNote: '帳戶待啟用，我們正在完成設定。',
    footerNote: '各帳戶按其自身幣種獨立計價，帳戶之間資金不互通。如需開立新交易帳戶，請聯絡客服。',
    // 介面 7.0：重置 MT5 交易密碼（只發連結，落地頁自設）
    resetMt5Pwd: '重置 MT5 密碼',
    resetMt5PwdTitle: '重置 MT5 交易密碼',
    resetMt5PwdDesc: '我們將向您的註冊電郵發送一封重置連結（30 分鐘內有效，僅可使用一次），請在電郵中設定新的交易密碼。重置的是 MT5 交易密碼，不影響用戶中心登入密碼，也不會退出目前登入。',
    resetMt5PwdSend: '發送重置連結',
    resetMt5PwdSent: '重置連結已發往 {email}，請查收電郵。',
    resetMt5PwdUnavailable: '目前帳戶狀態不支援重置交易密碼。',
    // 原型 v-myaccount「開戶日期」= MT5 開戶成功時點（AccountRow.openedTime， 定稿、待後端部署）
    openedTime: '開戶日期',
    // 模擬帳戶在這一頁唯讀展示（不參與目前帳戶/出入金/預警，R-15）
  },

  profile: {
    dob: '出生日期',
    amlScreening: 'AML 篩查',
    taxResidency: '稅務居民身份',
    // 原型寫「TFN 已提供」；我們只在境外納稅時收 TIN，澳洲稅務居民沒有 TFN 欄位
    tinProvided: 'TIN 已提供',
    // 客戶答過「境外納稅＝是」但沒填國家時的兜底：不猜澳洲
    taxCountryUnknown: '國家/地區未填寫',
    lastLogin: '最近登入',
    amlPassed: '已通過',
    corpNote: '本頁顯示的是您本人的認證與文件。公司主體資料與其他相關人士的認證狀態請見：',
    corpLinkCompany: '公司資訊',
    corpLinkPeople: '人員認證與文件',
    title: '個人資料',
    keyInfoNote: '如需更正姓名、出生日期等關鍵資訊，請聯絡客服並提供證明文件。',
    commTitle: '通訊偏好',
    basicTitle: '基本資訊',
    openedTime: '開通時間',
    fullName: '姓名',
    email: '電郵',
    phone: '手機號碼',
    address: '居住地址',
    // 介面 7.0：通知語言（只影響我們發給客戶的電郵；介面語言用頂欄切換，兩者解耦）
    notifyLocale: '通知語言',
    notifyLocaleHint: '僅影響我們發送給您的電郵所使用的語言。介面語言請使用右上角的語言切換。',
    notifyLocaleSaved: '通知語言已更新。',
    // 語言名按各自文字寫，三種介面下都一樣（2026-10-12 加繁體）
    localeZh: '简体中文',
    localeZhHk: '繁體中文',
    localeEn: 'English',
    // 姓名 / 電郵 / 手機修改一律走客服（§14.2）
    contactCsToChange: '聯絡客服修改',
    contactCsHintEmail: '修改電郵地址需要我們核驗您的身份並審批變更。請聯絡客服 {email}。',
    contactCsHintPhone: '修改手機號碼需要我們核驗您的身份並審批變更。請聯絡客服 {email}。',
    contactCsHintAddress: '修改居住地址需要我們核驗新地址證明並審批變更。請聯絡客服 {email}。',

    docTitle: '認證與文件',
    idProof: '身份證明',
    addressProof: '地址證明',
    verified: '已驗證',
    notVerified: '未驗證',
    docExpiryOn: '證件有效期 {date}',
    reverify: '立即認證',
    // 表述為「我們的帳戶政策」，不得寫監管要求（§3）
    docPolicyNote: '我們會不定期請您更新資料，以保持帳戶資訊準確，包括在身份證件到期前完成更新，以及每兩年提供一次新的地址證明。',

    securityTitle: '安全',
    closeAccountTitle: '關閉帳戶',
    closeAccountNote: '如需關閉帳戶，請聯絡客服 {email}。辦理前需滿足：所有持倉已平倉、帳戶餘額為零，且沒有正在處理的存提款。',
  },

  /* 補填手機號碼彈窗（D-98，cfd-platform 決策 029）：恢復登入時手機號碼被清空的客戶，登入後先補填。文案待產品確認 */
  phoneFill: {
    title: '請補填手機號碼',
    desc: '為保障帳戶安全，請填寫您本人在用的手機號碼後繼續使用用戶中心。',
    numberLabel: '手機號碼',
    codeLabel: '國際區號',
    // 與開戶站 s3a 同句（web-onboarding errors.phoneAu）
    invalidAu: '請輸入有效的澳洲電話號碼：10 位數字，以 02、03、04、07 或 08 開頭。',
    invalid: '請輸入有效的手機號碼：4–20 位數字。',
    saved: '手機號碼已更新',
  },

  bank: {
    bankCountry: '銀行所在國家',
    bankCountryHint: '非澳洲銀行請填兩位國家代碼（如 NZ）；澳洲銀行可留空。',
    // UAT CP-015：境外電匯需要收款行地址，必填
    bankAddress: '銀行地址',
    bankAddressHint: '請填寫開戶行的完整地址（街道、城市、國家），國際轉帳需要。',
    bankAddressRequired: '請填寫銀行地址',
    swiftHint: '國際轉帳時填寫，選填。',
    currencyHint: '須與該銀行帳戶的實際開戶幣種一致；不一致會導致出金被銀行退回。',
    presetTag: '開戶時登記',
    presetNote: '開戶時登記',
    // 需求 §12：審核中的帳號顯示提案號與提交日期（BankAccountView.proposalNo / submittedTime， 定稿、待後端部署）
    proposalNo: '提案號',
    submittedTime: '提交於',
    presetChange: '變更請聯絡客服',
    presetChangeTitle: '變更銀行帳戶',
    presetChangeBody: '該帳戶是開戶時隨申請資料一併登記的，屬於開戶檔案的一部分。如需變更，請聯絡客服辦理，我們會核對資料後為您更新。',
    title: '銀行帳號',
    add: '新增銀行帳號',
    listEmpty: '您還沒有新增銀行帳號，請點擊「+ 新增銀行帳號」開始。付款須來自 ZR Markets 帳戶持有人本人名下、已核驗的銀行帳戶，我們不接受第三方付款。',
    bankName: '銀行名稱',
    holderName: '帳戶名稱',
    bsb: 'BSB',
    accountNo: '帳號',
    accountTail: '帳號後四位',
    swift: 'SWIFT',
    currency: '帳戶幣種',
    statement: '銀行結單',
    statementHint: '請上傳近 12 個月內的銀行結單，用於證明帳戶歸屬。',
    statementRequired: '請上傳銀行結單',
    // 42215（結單 ref 失效）直接顯示後端 message，這裏不再有 statementInvalid
    pendingReview: '審核中',
    approved: '已審核',
    rejected: '已拒絕',
    submitted: '已提交審核，通過後即可用於出金。',
    // §12 校驗
    holderMustMatch: '帳戶持有人須與開戶姓名「{name}」完全一致。按我們的反洗錢政策，僅接受本人名下帳戶。',
    bsbInvalid: 'BSB 須為 6 位數字',
    accountNoInvalid: '請填寫帳號',
    onePendingOnly: '您已有一張銀行帳號在審核中，請等待審核結果後再提交。',
    thirdPartyNote: '付款須來自 ZR Markets 帳戶持有人本人名下、已核驗的銀行帳戶。我們不接受第三方付款。',
  },

  upload: {
    choose: '選擇文件',
    uploading: '上傳中…',
    remove: '移除',
    limits: '支援 PDF / JPG / PNG，單個文件不超過 10MB。',
    // 這兩句只用於發請求前的本地預檢；服務端拒絕（41311 / 42213 / 42214 / 50312）直接顯示後端 message
    tooLarge: '文件超過 10MB，請壓縮後再上傳。',
    badType: '僅支援 PDF、JPG、PNG 格式。',
    notFound: '文件不存在或已失效，請重新上傳。',
  },

  /**
   * 資金三頁共用的文案（介面 4.0 交付 /api/c/funding/** 後新增）。
   *
   * ⚠️ §3 文案紅線：**不得出現「免費 / 零手續費 / no fee」**。
   * 一期入金出金都不收費，做法是**整頁不設手續費欄位**，
   * 而不是寫一句「本次不收取手續費」——那同樣構成誘導表述。
   */
  funding: {
    account: '交易帳戶',
    proposalNo: '申請編號',
    viewRecords: '查看記錄',
    availableLabel: '可用',
    noAccount: '您目前沒有可用於資金操作的交易帳戶。如需協助，請聯絡客服。',
    // 凍結帳戶的資金方向由後端裁決，前端不寫死（見 dicts.ts MT5_ABILITY）
    frozenMayReject: '該帳戶目前已凍結、禁止交易，本次申請可能不被受理，以提交結果為準。',
    noRate: '該幣種方向暫無可用匯率，暫時無法提交跨幣種申請。',
    voucherInvalid: '匯款憑證無效或已失效，請重新上傳。',
  },

  deposit: {
    addBank: '+ 新增銀行帳戶',
    title: '入金',
    // §10.1：附言帳號隨所選帳戶變化，是後台核帳歸屬的關鍵
    remarkHint: '轉帳附言請填寫您的 MT5 帳號（{login}），以便我們快速核帳入帳。',
    currency: '存款幣種',
    amount: '存款金額',
    amountPh: '請輸入存款金額',
    remitDate: '匯款日期',
    remitDateHint: '選填。填寫後有助於我們更快核對到帳。',
    payer: '付款銀行帳戶',
    payerHint: '僅可使用您本人已通過審核的銀行帳戶付款。',
    noPayer: '您還沒有已通過審核的銀行帳戶，請先在「銀行帳號」中新增。',
    voucher: '匯款憑證',
    voucherHint: '請上傳本次匯款的銀行收據或轉帳截圖。',
    expected: '預計入帳',
    // §10.1 跨幣種引導句 + 即時匯率行（不帶獲取時間）
    fxLead: '所選帳戶（{login}）的帳戶貨幣為 {cur}，{from} 入金將按即時匯率自動兌換入帳。',
    liveRate: '即時匯率 1 {from} = {rate} {to}',
    // §10.1 頁面底部條款（澳洲版）—— 反洗錢表述為「我們的反洗錢政策」，不寫監管要求
    termsTitle: '入金須知',
    terms1: '付款須來自 ZR Markets 帳戶持有人本人名下、已核驗的銀行帳戶。我們不接受第三方付款。',
    terms2: '款項到帳並完成核帳後，我們會存入您的交易帳戶，並透過電郵向您同步進度。',
    terms3: '提交存款通知即表示您確認資金來源合法，並已閱讀 PDS、FSG 與《客戶協議》。',
    terms4: '以其他幣種存入的款項，將按處理您存款時適用的匯率轉換，可能與頁面顯示的參考匯率不同。',
    receiving: '公司收款帳戶',
    receivingNote: '請使用您本人的銀行帳戶，向下列帳戶匯款後再提交本頁表單。',
    noReceiving: '目前幣種暫無可用的收款帳戶，請聯絡客服。',
    bankTransfer: '銀行轉帳',
    accountName: '帳戶名稱',
    bankName: '銀行名稱',
    bankAddress: '銀行地址',
    accountNo: '帳號',
    submit: '提交存款通知',
    submitNote: '提交後我們會在收到款項並核對無誤後為您入帳，入帳結果將以電郵通知。',
    submitted: '存款通知已提交',
    submittedNote: '我們會在核對到帳後為您入帳，處理進度可在存款記錄中查看。',
    another: '再提交一筆',
  },

  withdraw: {
    // 形態按原型 V1.4.1：三張步驟卡 + 彙總卡 + 勾選確認；文案以需求 V1.4 §11 與原型為準
    title: '出金',
    stepAccount: '從哪個交易帳戶出金',
    stepAmount: '出金金額（{cur}）',
    stepBank: '收款銀行帳號',
    accountCurrency: '帳戶幣種',
    // §11.2：出金頁用「可提」，入金頁與頂欄仍用「可用」
    withdrawableLabel: '可提',
    amount: '出金金額',
    fillMax: '全部提取',
    overLimit: '出金金額不能超過帳戶 {login} 的可提金額（{amount} {cur}）',
    availNote: '可提金額由系統按該帳戶的餘額、淨值與保證金佔用計算。',
    unsettledNote: '另有 {amount} {cur} 近期入金尚在清算中，暫不可提取。',
    // §11.1 三重校驗：無帳號 → 引導新增；均審核中 → 展示審核中狀態
    noBankTitle: '請先新增銀行帳號',
    noBankBody: '出金需轉入您本人名下、已通過審核的銀行帳號。請先前往「資金管理 → 銀行帳號」新增並上傳結單。',
    goBank: '前往新增銀行帳號',
    bankPendingTitle: '銀行帳號審核中',
    bankPendingBody: '您的銀行帳號正在審核中，審核通過後即可發起出金（結果將電郵通知）。',
    holder: '帳戶名稱',
    manageBanks: '管理',
    // 入帳幣種不再單獨選，隨所選收款帳號聯動（原型 V1.4.1）
    creditCurrency: '入帳幣種（銀行帳戶幣種）',
    creditFollows: '隨所選收款銀行帳號聯動',
    noSameCurrencyBank: '您沒有 {cur} 幣種的已審核銀行帳號，出金將按參考匯率換算入帳。',
    refRate: '目前匯率（參考）',
    expected: '預計入帳',
    expectedIn: '預計入帳（{cur}）',
    transferNote: '僅轉帳至您本人名下帳戶，通常最多 5 個工作天到帳。',
    // R-16：提交即扣款，勾選確認代替二次確認彈窗
    ack: '我了解提交後金額會立即從我的 MT5 帳戶扣除，且無法在用戶中心撤銷；若申請未獲批准，資金將退回我的交易帳戶。',
    submit: '提交出金申請',
    // V1.4 §8.4：凍結帳戶允許出金，只提示其禁止入金
    frozenNote: '帳戶 {login} 目前已凍結，臨時禁止交易：暫不能入金，但仍可正常提取該帳戶資金。',
    currencySwitched: '計價幣種已切換為 {cur}',
    expectedEstimated: '預計入帳（按提交時參考匯率估算）',
    // 後端 WithdrawResult.expectedAmount：參考值，執行匯率審批時才固化 —— 不能寫「到帳金額」
    expectedReference: '參考入帳金額（以審批時執行匯率為準）',
    accountLine: '{login}（{type}）',
    submitted: '出金申請已提交',
    another: '再提交一筆',
    // 50321：扣款結果未知且未建單 —— 頁面必須停住，且不給重試
    unknownTitle: '申請結果確認中',
    unknownNote: '本次提交的處理結果尚未確認，我們正在核對您帳戶的扣款狀態。',
    unknownNoRetry: '請勿重複提交本次申請。請稍後在提款記錄中查看結果，如有疑問請聯絡客服。',
  },

  records: {
    depositSettled: '存款記錄',
    depositApplications: '入金申請記錄',
    withdrawalSettled: '提款記錄',
    withdrawalApplications: '出金申請記錄',
    allAccounts: '全部帳戶',
    // §13：報表時間口徑與站點其餘位置不同，必須在表頭註明；金額按記錄所屬交易帳戶的幣種計價（§8.1，多帳戶下不是固定 AUD）
    mt5TimeNote: '下表時間均為 MT5 伺服器時間（GMT+3）；金額以該筆記錄所屬交易帳戶的幣種計價。',
    applyTime: '申請時間',
    // 6.0 補：到帳時間 / 匯率 / 憑證
    settledTime: '到帳時間',
    rate: '匯率',
    voucher: '憑證',
    export: '匯出',
    viewVoucher: '查看',
    voucherOpenFailed: '憑證暫時無法打開，請稍後重試',
    // 6.0 補：申請時間範圍篩選（§13 通用篩選條）
    rangeAll: '全部時間',
    range7d: '近 7 天',
    range30d: '近 30 天',
    range90d: '近 90 天',
    rangeYtd: '今年',
    // 列名按需求 §13 表 / 原型 V1.4.1
    proposalNo: '提案號',
    account: '交易帳戶',
    channel: '渠道',
    amount: '金額',
    settledAmount: '到帳金額（帳戶幣種）',
    expectedAmount: '預計到帳（帳戶幣種）',
    dealIdNote: 'MT5 Deal ID 為該筆出入金在 MT5 帳戶中的 balance 成交編號，可用於與交易平台對帳。',
    dealId: 'MT5 Deal ID',
    status: '狀態',
    empty: '暫無記錄。',
    settledEmptyHint: '這裏只顯示已完成的記錄；處理中的申請請看',
    // 申請頁籤「操作 · 重新發起」與表下「狀態說明」（需求 §13 / §13.1；statusCode 由 定稿、待後端部署）。
    // 說明文案前端靜態配，按 kind.CODE 取；措辭守 §3：不出現「監管要求」「免費」，退款口徑與 R-16 披露句一致
    action: '操作',
    reinitiate: '重新發起',
    statusNotesTitle: '狀態說明',
    statusHint: {
      deposit: {
        PROCESSING: '處理中：我們已收到您的存款通知，正在核對款項到帳情況。',
        SETTLED: '已到帳：款項已入帳至您的 MT5 交易帳戶。',
        CANCELLED: '已取消：本次存款通知未完成處理。如您已完成匯款，請重新發起並上傳匯款憑證。',
      },
      withdrawal: {
        REVIEWING: '審核中：金額已從您的 MT5 交易帳戶扣除，正在審核處理。',
        COMPLETED: '已完成：款項已付至您的收款銀行帳戶。',
        REFUNDED: '已退回：本次申請未獲通過，金額已退回您的 MT5 交易帳戶，您可重新發起。',
        REJECTED: '未成功：本次申請已取消或已超時，扣除的金額將退回您的 MT5 交易帳戶，您可重新發起。',
      },
    },
    pageInfo: '第 {page} / {max} 頁，共 {total} 條',
    prev: '上一頁',
    next: '下一頁',
  },

  platform: {
    corpHolderNote: '帳戶持有人為 {company}，授權代表憑 MT5 帳號與交易密碼進行交易。',
    title: '平台下載',
    liveTag: 'Live',
    demoTag: 'Demo',
    liveTitle: '真實帳戶',
    demoTitle: '模擬帳戶',
    loginAccount: '登入帳號',
    server: '伺服器',
    password: '密碼',
    passwordValue: '開戶電郵中發送給您的 MT5 交易密碼（與用戶中心登入密碼不同）',
    // 帳戶尚未開出來時替代空白的帳號 / 幣種兩行
    noAccountYet: '交易帳戶還在開通中，帳號與幣種開通後會顯示在這裏。您現在就可以先下載並安裝客戶端，伺服器名按下方填寫即可。',
    // 純下載態：這一頁對拿不到帳戶的客戶（申請被拒 / 查重 / 年齡或適當性未過 / 未提交）只留下載連結
    downloadOnlyNote: '您可以在這裏下載 MetaTrader 5 客戶端。登入帳號與伺服器資訊會在交易帳戶開通後顯示在本頁。',
    demoPasswordNote: '模擬帳戶密碼已在開通時發送至您的電郵。',
    // 兩個伺服器名僅一詞之差，不提示極易登錯（§15）
    serverWarn: '真實帳戶與模擬帳戶位於不同伺服器，登入時請選擇與帳號對應的伺服器。',
    // §15 原文口徑：7.0 起有了重置端點，文案改回指向「我的帳戶」（此前因無端點寫成「聯絡客服」）
    notSameAsPortal: 'MT5 交易密碼與用戶中心登入密碼不同。忘記可在「帳戶管理 → 我的帳戶 → 重置 MT5 密碼」重新設定。',
    multiAccountNote: '目前顯示交易帳戶 {login}（{type}）。您共有 {n} 個真實交易帳戶，可在頂欄切換帳戶查看對應的登入帳號。',
    downloadTitle: '下載 MetaTrader 5',
    windows: 'Windows',
    macos: 'macOS',
    ios: 'iOS',
    android: 'Android',
    demoNotOpened: '模擬帳戶尚未開通',
  },

  support: {
    title: '幫助與支援',
    desc: '點擊下方按鈕會自動打開您裝置上的電郵程式，向我們的客服團隊發送電郵。',
    sendMail: '發送電郵給客服',
    subject: '客戶諮詢 — ZR 投資者門戶',
    // 裝置無預設電郵客戶端時點擊無反應，須同時給可複製的地址（§16）
    fallback: '如果點擊後沒有反應，說明您的裝置未設定預設電郵程式。請手動發送電郵至：',
  },

  dict: {
    /** 公司相關人士角色（V1.4 §6A） */
    corpPersonRole: {
      AUTH_REP: '授權代表',
      DIRECTOR: '董事',
      UBO: '最終實益擁有人',
    },
    /** 相關人士認證狀態：開戶前 6 態；REMOVED=已移除，不計入門檻 */
    corpPersonStatus: {
      TODO: '待認證',
      SENT: '已發送連結',
      REVIEW: '審核中',
      PASSED: '已通過',
      RESUBMIT: '需補件',
      FAILED: '未通過',
      REMOVED: '已移除',
    },
    /** 開戶後的證件狀態（5 態） */
    docState: {
      VALID: '有效',
      EXPIRING: '即將到期',
      EXPIRED: '已過期',
      // 原型口徑是「審核中」：這一檔既包括剛提交待出結果，也包括 Sumsub 判否後轉人工，
      // 客戶要看到的是「在我們手上」，而不是「你在重認」（他可能什麼都沒做）
      REVIEW: '審核中',
      // v1 遷移沒帶來有效期欄位，待補錄——不是「已過期」
      NOT_COLLECTED: '有效期待補錄',
    },
    /** 證件類型（相關人士確認卡；未知值原樣顯示） */
    idType: {
      PASSPORT: '護照',
      DRIVER_LICENCE: '駕駛執照',
      NATIONAL_ID: '身份證',
    },
    /** 客戶分類（s761G） */
    clientClassification: {
      WHOLESALE_S761G: '公司客戶 Wholesale（s761G）',
      SOPHISTICATED: '公司客戶 Sophisticated',
    },
    mt5Status: {
      PENDING: '待啟用',
      ENABLED: '正常',
      FROZEN: '已凍結·禁止交易',
      DISABLED: '已禁用',
      CLOSED: '已關閉',
      CHANGING: '變更中',
    },
    // 出入金渠道（介面 channel 欄位，枚舉碼；未知值原樣顯示）
    channel: {
      BANK_TRANSFER: '銀行轉帳',
      PAYID: 'PayID',
    },
    bankStatus: {
      PENDING_REVIEW: '審核中',
      APPROVED: '已審核',
      REJECTED: '已拒絕',
      REPLACED: '已替換',
    },
    // 客戶可見的開戶狀態標籤只有兩種（§3 狀態術語口徑）
    facing: {
      IN_PROGRESS: '待完成開戶',
      DECLINED: '開戶未通過',
      DONE: '',
    },
  },

  footer: {
    // 官網英文原文，中文模式下不翻譯（§3 頁腳口徑）
    entity: 'Zircon Financial Service (Australia) Pty Ltd',
    acn: 'ACN 672 642 054',
    afsl: 'AFSL 562700',
    // 業務 09-24：持牌主體行末尾加 trading as ZR Markets；頁腳英文原文，中文模式同樣不翻譯
    tradingAs: 'trading as ZR Markets',
    risk: 'Trading in CFDs carries a high level of risk and may not be suitable for all investors. You may lose more than your initial investment. Please read our {pds}, {fsg} and {tmd} before deciding to trade.',
  },
}

export default messages
